/**
 * Reusable Tailwind class combinations for consistent styling across the app.
 * Use these constants for passive/layout elements.
 * For interactive elements (buttons, inputs), prefer UI components from components/ui/
 */

/**
 * Card and container backgrounds
 */
export const CARDS = {
  /** Standard card with glass effect - used for main section cards */
  base: 'rounded-xl border border-gray-200 dark:border-white/10 bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm p-6 shadow-sm',

  /** List item card with gradient background */
  listItem: 'p-3 md:p-4 rounded-xl border border-gray-200 dark:border-white/10 bg-gradient-to-r from-gray-100 dark:from-white/5 to-transparent',

  /** Compact list item for dense layouts */
  listItemCompact: 'p-2 md:p-3 rounded-lg border border-gray-200 dark:border-white/10 bg-gradient-to-r from-gray-100 dark:from-white/5 to-transparent',

  /** Glass morphism card - more subtle */
  glass: 'rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm',

  /** Nested card (card within a card) */
  nested: 'rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 p-4',
} as const;

/**
 * Container layouts
 */
export const CONTAINERS = {
  /** Main page container */
  page: 'max-w-7xl mx-auto p-4 md:p-6',

  /** Section container with max width */
  section: 'max-w-4xl mx-auto space-y-6',

  /** Full width section */
  sectionFull: 'w-full space-y-6',

  /** Scrollable container */
  scrollable: 'flex-1 overflow-auto pr-1 min-h-0',
} as const;

/**
 * Input and form field styles
 */
export const INPUTS = {
  /** Standard text input */
  base: 'form-field',

  /** Full width input */
  full: 'form-field max-w-none',

  /** Textarea */
  textarea: 'form-field min-h-[100px] resize-y',

  /** Select dropdown */
  select: 'form-field cursor-pointer',
} as const;

/**
 * Text and typography
 */
export const TEXT = {
  /** Page heading (h1) */
  h1: 'text-2xl md:text-3xl font-bold text-gray-900 dark:text-white',

  /** Section heading (h2) */
  h2: 'text-xl md:text-2xl font-semibold text-gray-900 dark:text-white',

  /** Subsection heading (h3) */
  h3: 'text-lg font-semibold text-gray-800 dark:text-slate-100',

  /** Body text */
  body: 'text-sm md:text-base text-gray-700 dark:text-slate-300',

  /** Small text / helper text */
  small: 'text-xs md:text-sm text-gray-600 dark:text-slate-400',

  /** Muted text */
  muted: 'text-xs text-slate-400',

  /** Label */
  label: 'block text-sm font-medium text-gray-700 dark:text-slate-300',

  /** Eyebrow / overline */
  eyebrow: 'text-xs font-semibold uppercase tracking-wider text-primary-500',
} as const;

/**
 * Borders and dividers
 */
export const BORDERS = {
  /** Standard border */
  default: 'border border-gray-200 dark:border-white/10',

  /** Top border divider */
  top: 'border-t border-gray-200 dark:border-white/10',

  /** Bottom border divider */
  bottom: 'border-b border-gray-200 dark:border-white/10',

  /** Danger border */
  danger: 'border border-red-200 dark:border-red-500/30',

  /** Success border */
  success: 'border border-green-200 dark:border-green-500/30',
} as const;

/**
 * Interactive states (for non-button elements)
 */
export const INTERACTIVE = {
  /** Glass hover effect */
  glassHover: 'glass-hover',

  /** Clickable card */
  clickableCard: 'cursor-pointer transition-all hover:shadow-lg hover:scale-[1.01] active:scale-[0.99]',

  /** Subtle hover for list items */
  hoverSubtle: 'transition-colors hover:bg-gray-50 dark:hover:bg-white/5',
} as const;

/**
 * Alert and status boxes
 */
export const ALERTS = {
  /** Error alert */
  error: 'p-4 rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-200',

  /** Warning alert */
  warning: 'p-4 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-200',

  /** Success alert */
  success: 'p-4 rounded-lg border border-green-200 dark:border-green-500/30 bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-200',

  /** Info alert */
  info: 'p-4 rounded-lg border border-cyan-200 dark:border-cyan-500/30 bg-cyan-50 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-200',
} as const;

/**
 * Stat pills and badges
 */
export const STATS = {
  /** Small inline stat */
  pill: 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-200 dark:bg-white/10 text-xs',

  /** Stat with separator dots */
  withDot: 'flex items-center gap-1.5 text-xs text-slate-400',

  /** Dot separator */
  dot: 'h-1 w-1 rounded-full bg-white/30',
} as const;

/**
 * Layout utilities
 */
export const LAYOUT = {
  /** Flex row with gap */
  row: 'flex items-center gap-2',

  /** Flex row with space between */
  rowBetween: 'flex items-center justify-between gap-2',

  /** Flex column with gap */
  column: 'flex flex-col gap-2',

  /** Grid with responsive columns */
  grid2: 'grid grid-cols-1 md:grid-cols-2 gap-4',
  grid3: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4',

  /** Stack spacing */
  stack: 'space-y-3',
  stackLg: 'space-y-6',
} as const;
